// Moved from `src/hooks/mutation/useMutateUpdateSessionClinicalTask.ts` (now a
// delegate) with the D2 family (ARCFE-945). Cohort B: the success analytics
// fired only when the optimistic snapshot held the task (no else branch, unlike
// the global update hook) — `onUpdateSuccess` carries `previousTask` and the
// delegate replays exactly that conditional; the error path ran logger →
// invalidate → toast, so `onUpdateError` fires after the invalidate
// (ARCFE-921). Param interface stays `UpdateSessionClinicalTaskParams` — it was
// already collision-free.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import { logger } from '@heidi/runtime/logger';
import type { ClinicalTaskCategory, ClinicalTaskStatus } from '@heidi/schema/ml-scribe';

import {
  GET_CLINICAL_TASKS,
  GET_CLINICAL_TASKS_BY_SESSION_ID,
} from '../../contracts/keys';
import type {
  ClinicalTask,
  ClinicalTaskResponse,
  GetClinicalTasksResponse,
} from '../../contracts/types';
import { updateClinicalTask } from '../api/clinical-tasks';

export interface UpdateSessionClinicalTaskParams {
  taskId: string;
  sessionId: string;
  taskData: {
    title?: string;
    description?: string;
    category?: ClinicalTaskCategory;
    status?: ClinicalTaskStatus;
    source_quote?: string | null;
    is_conditional?: boolean;
    is_suggestion_dismissed?: boolean;
    due_date?: string | null;
  };
}

interface MutationContext {
  previousTask?: ClinicalTask;
}

interface UseMutateUpdateSessionClinicalTaskProps {
  /** App-reaction port: fired where the legacy toast fired (ARCFE-921). */
  onUpdateError?: (error: AxiosError) => void;
  /** App-reaction port: fired where the legacy analytics dispatch fired. */
  onUpdateSuccess?: (data: ClinicalTaskResponse, previousTask: ClinicalTask) => void;
}

export const useMutateUpdateSessionClinicalTask = (
  props?: UseMutateUpdateSessionClinicalTaskProps
) => {
  const queryClient = useQueryClient();

  return useMutation<
    ClinicalTaskResponse,
    AxiosError,
    UpdateSessionClinicalTaskParams,
    MutationContext
  >({
    mutationFn: async ({ taskId, taskData }) => {
      const { data } = await updateClinicalTask(taskId, taskData);
      return data;
    },
    onMutate: ({ taskId, sessionId, taskData }): MutationContext => {
      const oldTasks = queryClient.getQueryData<GetClinicalTasksResponse>([
        GET_CLINICAL_TASKS_BY_SESSION_ID,
        { sessionId },
      ]);
      const previousTask =
        oldTasks?.tasks.find((task) => task.id === taskId) ?? undefined;

      queryClient.setQueryData<GetClinicalTasksResponse>(
        [GET_CLINICAL_TASKS_BY_SESSION_ID, { sessionId }],
        (old) => {
          if (!old || !old.tasks) return old;
          return {
            ...old,
            tasks: old.tasks.map((task) =>
              task.id === taskId ? { ...task, ...taskData } : task
            ),
          };
        }
      );
      return { previousTask };
    },
    onError: (error, { sessionId }) => {
      logger.error('Error updating clinical task:', error.response?.data);
      void queryClient.invalidateQueries({
        queryKey: [GET_CLINICAL_TASKS_BY_SESSION_ID, { sessionId }],
      });
      props?.onUpdateError?.(error);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: [GET_CLINICAL_TASKS],
      });
    },
    onSuccess: (data, _, context) => {
      if (context?.previousTask) {
        props?.onUpdateSuccess?.(data, context.previousTask);
      }
    },
  });
};
